import {
  Box,
  Checkbox,
  Flex,
  Img,
  Text,
  Spinner,
  IconButton,
  VisuallyHidden,
  Input as ChakraInput,
} from "@chakra-ui/react";
import React, { useRef, useState } from "react";
import colors from "../../utils/colors";
import { person } from "../../components/defaultHeader/assets";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useNavigate } from "react-router-dom";
import { EditIcon } from "@chakra-ui/icons";
import { right } from "../../assets";
import { useUploadMedia } from "../../apis/patients";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { setDoctorInfo } from "../../store/authSlice";
import { TDoctorInfo } from "../../apis/types";
import customToast, { ToastType } from "../../components/Toast";

const ProfileCategoriesCard = ({ active }: { active?: string }) => {
  const { doctorInfo } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const name = doctorInfo?.name || "Doctor";

  const profilePictureObject = doctorInfo?.profilePicture as
    | { url: string }
    | undefined
    | null;
  const displayProfilePictureUrl = profilePictureObject?.url || person;

  const { mutate: uploadProfilePicture } = useUploadMedia();

  const handleEditPictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file && doctorInfo?._id) {
      const formData = new FormData();
      formData.append("file", file);
      setIsUploading(true);

      uploadProfilePicture(
        { formData, mediaType: "profile-picture" },
        {
          onSuccess: (data: any) => {
            const newProfilePictureUrlFromServer = data?.data?.url;
            if (newProfilePictureUrlFromServer && doctorInfo) {
              const updatedDoctorInfo: TDoctorInfo = {
                ...doctorInfo,
                profilePicture: { url: newProfilePictureUrlFromServer } as any,
              };
              dispatch(setDoctorInfo(updatedDoctorInfo));

              const sessionDoc = sessionStorage.getItem("created-doctor");
              if (sessionDoc) {
                try {
                  const parsedSessionDoc = JSON.parse(
                    sessionDoc
                  ) as TDoctorInfo;
                  const updatedSessionDoc = {
                    ...parsedSessionDoc,
                    profilePicture: {
                      url: newProfilePictureUrlFromServer,
                    } as any,
                  };
                  sessionStorage.setItem(
                    "created-doctor",
                    JSON.stringify(updatedSessionDoc)
                  );
                } catch (e) {
                  console.error(
                    "Error updating session storage for profile picture",
                    e
                  );
                }
              }
              customToast(
                "Profile picture updated successfully!",
                ToastType.success
              );
            } else {
              customToast(
                "Failed to get new picture URL from server.",
                ToastType.error
              );
            }
            setIsUploading(false);
          },
          onError: (error: any) => {
            customToast(
              error?.response?.data?.message || "Upload failed",
              ToastType.error
            );
            setIsUploading(false);
          },
        }
      );
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const options = [
    {
      title: "Contact information",
      subtitle: "Name, Email",
      actions: () => navigate("/profile/contact-information"),
    },
    {
      title: "Medical license",
      subtitle: "License, Specialization, etc",
      actions: () => navigate("/profile/medical-license"),
    },
    {
      title: "Clinic address",
      subtitle: "Address of physical consultations",
      actions: () => navigate("/profile/clinic-address"),
    },
    {
      title: "Availability",
      subtitle: "Available days and time",
      actions: () => navigate("/profile/availability"),
    },
  ];

  return (
    <Box
      border={{ base: "none", md: `1px solid ${colors.grey}` }}
      boxShadow={{ base: "none", md: `1px 4px 15px 0px #3030301F` }}
      width={"100%"}
      borderRadius={{ base: "0", md: "8px" }}
      backgroundColor={colors.white}
      pb={{ base: "20px", md: "0" }}
    >
      <Flex
        direction={"column"}
        gap={{ base: "24px", md: "32px" }}
        alignItems={"center"}
        py={{ base: 4, md: 6 }}
      >
        <Box position="relative" mt={{ base: "0", md: "20px" }}>
          <Img
            src={displayProfilePictureUrl}
            boxSize={{ base: "80px", md: "100px" }}
            alt={`${name}'s profile`}
            borderRadius="full"
            objectFit="cover"
            border={`2px solid ${colors.primary}`}
          />
          {isUploading ? (
            <Spinner
              size="xl"
              color={colors.primary}
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
            />
          ) : (
            <IconButton
              aria-label="Edit profile picture"
              icon={<EditIcon />}
              size="sm"
              isRound
              colorScheme="blue"
              variant="solid"
              position="absolute"
              bottom="0"
              right="0"
              onClick={handleEditPictureClick}
              boxShadow="md"
            />
          )}
          <VisuallyHidden>
            <ChakraInput
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </VisuallyHidden>
        </Box>

        <Text
          fontFamily={"lato"}
          fontWeight={700}
          fontSize={{ base: "18px", md: "20px" }}
          lineHeight={"24px"}
          color={colors.dark}
          textAlign="center"
        >
          {doctorInfo?.title
            ? `${doctorInfo.title} ${doctorInfo?.name ?? ""}`.trim()
            : doctorInfo?.name ?? "User"}
        </Text>

        <Flex direction={"column"} width={"100%"} alignItems={"center"}>
          {options.map((option, index) => (
            <Flex
              key={option.title}
              width={"100%"}
              direction={"column"}
              alignItems={"center"}
              borderBottom={
                index !== options.length - 1
                  ? {
                      base: `1px solid ${colors.lightGrey}`,
                      md: `1px solid ${colors.grey}`,
                    }
                  : "0px"
              }
              cursor={"pointer"}
              bg={active === option.title ? colors.lightGreen : "transparent"}
              _hover={{
                background:
                  active !== option.title
                    ? colors.lightGrey
                    : colors.lightGreen,
              }}
              onClick={() => option.actions()}
              py={{ base: "12px", md: "0" }}
            >
              <Flex
                justifyContent={"space-between"}
                alignItems={"center"}
                padding={"16px"}
                width={"calc(100% - 32px)"}
              >
                <Flex gap={{ base: "12px", md: "20px" }} alignItems={"center"}>
                  <Checkbox
                    colorScheme="green"
                    isChecked={active === option.title}
                    size={{ base: "md", md: "lg" }}
                    isReadOnly
                  />
                  <Box>
                    <Text
                      fontFamily={"lato"}
                      fontWeight={active === option.title ? 600 : 400}
                      fontSize={{ base: "15px", md: "16px" }}
                      lineHeight={"19.2px"}
                      color={colors.dark}
                    >
                      {option.title}
                    </Text>
                    <Text
                      fontFamily={"lato"}
                      fontWeight={400}
                      fontSize={{ base: "13px", md: "14px" }}
                      lineHeight={"14.4px"}
                      color={colors.secondary}
                    >
                      {option.subtitle}
                    </Text>
                  </Box>
                </Flex>
                <Img src={right} boxSize={{ base: "10px", md: "12px" }} />
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Box>
  );
};

export default ProfileCategoriesCard;
